import React, {
  useMemo,
  useState,
  useEffect,
  useReducer,
  useContext,
  useRef,
} from 'react'
import DateRangePicker from '@wojtekmaj/react-daterange-picker/dist/entry.nostyle'
import Filters from 'components/Filters'
import Icon from 'components/Icon'
import Range from 'components/Range'
import Voice from 'components/Voice'
import VoiceGrid from 'components/VoiceGrid'
import VoiceDrawer from 'components/VoiceDrawer'
import FullBanner from 'components/FullBanner'
import classNames from 'classnames'
import debounce from 'debounce-promise'
import messages from './messages'
import tw from 'twin.macro'
import Meta from 'components/Meta'
import {
  Card,
  Banner,
  Button,
  Field,
  Tag,
  LoadingFull,
  SelectAsync,
  Select,
  Input,
  Checkbox
} from '@arnsbogroup/react-voicearchive'
import { useKeyboardShortcut } from '@arnsbogroup/react-voicearchive/lib/hooks'
import { FormattedMessage, useIntl } from 'react-intl'
import { useBrand } from 'contexts/BrandContext'
import { ProjectContext, withListener, intents } from 'contexts/ProjectContext'
import {
  Routes,
  Route,
  Link,
  useSearchParams,
  useNavigate,
  useMatch,
  useLocation,
} from 'react-router-dom'
import { messages as routeMessages } from 'routes'
import { getVoicesUrl, getVoiceUrl } from 'data/voices/list'
import { ContactHelpCard } from 'components/ContactInfo'
import useBreakpoint from 'hooks/useBreakpoint'
import MarkdownCopy from 'containers/MarkdownCopy'
import { reverseLookupMessage } from 'messageUtils'
import languageNames from 'data/languages/messages'
import useApi ,{ request } from 'hooks/useApi'
import useCurrentUser from 'hooks/useCurrentUser'
import { QualityCheckCard } from 'components/QualityCheckCard'
import InitialProcess from 'components/InitialProcess'
import languages from 'data/languages/list'
import NotFound from 'containers/NotFound'

const bpCols = { sm: 1, md: 2, lg: 3, xl: 3, '2xl': 3 }

// Coordinator options for the "New" filter, in months back.
const NEW_MONTH_OPTIONS = [1, 2, 3, 6]

// Everyone else can only filter on this fixed window (also enforced by the API).
const PUBLIC_NEW_MONTHS = 2

const NEW_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const toDateInputValue = (date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-')

const monthsAgoDate = (months) => {
  const date = new Date()
  date.setMonth(date.getMonth() - months)

  return toDateInputValue(date)
}

const ExplorePage = ({
  currentUser: { roles = [], onLogOut, name },
  project,
  dispatch,
  meta,
  voices,
  loading,
  loadingMore,
  onSuggestWithAI,
  characterMap,
  setCharacterMap,
}) => {
  const bp = useBreakpoint()
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const queryRef = useRef()
  const { formatMessage } = useIntl()
  const brand = useBrand()
  const [query, setQuery] = useState('')
  const [voiceGridListState, setVoiceGridList] = useState([])
  const { features = [] } = useCurrentUser()
  const [searchParams] = useSearchParams()
  const showAiSuggest = searchParams.get('utm_campaign') == 'ai-suggest-alpha' || features.includes('ai-script-to-suggestion')

  const [dialectOptions] = useApi('/rest/v2/dialect?pageSize=999');

  const languageOptions = languages
  .map((language) => ({
    id: language.uid,
    value: language.key,
    label: languageNames[language.key] ? formatMessage(languageNames[language.key]) : 'N/A',
  }))
  .sort((x, y) => {
    let a = x.label.toUpperCase(),
      b = y.label.toUpperCase()
    return a == b ? 0 : a > b ? 1 : -1
  })

  useEffect(() => {
    setQuery(project.query || '')
  }, [project.query])

  useKeyboardShortcut(['Escape'], () =>
    dispatch({
      type: 'SET_VOICES',
      payload: [],
    })
  )

  useKeyboardShortcut(
    ['Shift', '/'],
    () => {
      queryRef.current?.focus()
    },
    { overrideSystem: true }
  )

  useKeyboardShortcut(['ArrowLeft'], () => setFocusedIndex((i) => i - 1), {
    overrideSystem: true,
  })
  useKeyboardShortcut(['ArrowRight'], () => setFocusedIndex((i) => i + 1), {
    overrideSystem: true,
  })
  useKeyboardShortcut(
    ['ArrowUp'],
    () => setFocusedIndex((i) => Math.sign(i - bpCols[bp])),
    { overrideSystem: true }
  )
  useKeyboardShortcut(
    ['ArrowDown'],
    () => setFocusedIndex((i) => i + bpCols[bp]),
    { overrideSystem: true }
  )

  useEffect(() => {
    let voiceGridList = [];
    if (voices && voices.length > 0) {
      voiceGridList = voices.map((voice, i) => (
        <Voice
          id={`${i}-${voice.id}`}
          key={`${i}-${voice.id}`}
          voice={voice}
          dispatch={dispatch}
          selected={!!project.voices[voice.sku]}
          focused={!!project.voices[voice.sku] || i == focusedIndex}
          product={project.category}
          max={1}
          navigate={`/${formatMessage(routeMessages.explore).replace('/*', '')}/${voice.sku}`}
          readOnly={project.includeAll}
          onDownload={project.voices[voice.sku] ? undefined : (demo) => {
              dispatch({ type: 'ADD_VOICE', payload: { voice, demo, intent: intents.DOWNLOAD } })
          }}
          characterMap={characterMap}
        />
      ))
    }

    if (!roles.includes('coordinator') && !loading && voiceGridList.length) {
      if (voiceGridList.length < 12) {
        voiceGridList.splice(
          Math.floor(voiceGridList.length / 2),
          0,
          <ContactHelpCard key={`contact-0`} />
        )
      } else {
        voiceGridList.splice(8, 0, <ContactHelpCard key={`contact-8`} />)
      }
    }

    if (!roles.includes('coordinator') && !loading && voiceGridList.length) {
      if (voiceGridList.length > 3) {
        if (voiceGridList.length < 12) {
          voiceGridList.splice(
            Math.floor(voiceGridList.length / 3),
            0,
            <QualityCheckCard key={`contact-0`} />
          )
        } else {
          voiceGridList.splice(4, 0, <QualityCheckCard key={`contact-8`} />)
        }
      }
    }

    setVoiceGridList(voiceGridList);
  }, [voices, characterMap, project.voices]);

  function groupAgeTuple(ageTuple) {
    if (ageTuple[0] >= 0 && ageTuple[1] <= 18) {
      return '0-18';
    }
    if (ageTuple[0] >= 19 && ageTuple[1] <= 30) {
      return '19-30';
    }
    if (ageTuple[0] >= 31 && ageTuple[1] <= 50) {
      return '31-50';
    }
    if (ageTuple[0] >= 51 && ageTuple[1] <= 100) {
      return '51-100';
    }
    if (ageTuple[0] >= 18 && ageTuple[1] <= 100) {
      return '18-100';
    }
    return null;
  }

  const isCustomNewDate = NEW_DATE_PATTERN.test(project.newSince || '')

  const projectLanguageKeyExists = project?.language ?
    (typeof languageNames[reverseLookupMessage('languages', project?.language, formatMessage)] !== 'undefined'):
    false

  if (project?.language && !projectLanguageKeyExists) {
    return <NotFound />
  }

  return (
    <div className="h-full flex flex-col">
      <Meta language={projectLanguageKeyExists ? formatMessage(
        languageNames[reverseLookupMessage('languages', project?.language, formatMessage)]
      ) : undefined} />

      { showAiSuggest &&
        <FullBanner onClick={() => onSuggestWithAI()}>
          <FormattedMessage {...messages.aiSuggestBanner}
                            values={{
                              b: chunk => <b className='font-bold'>{chunk}</b>,
                              p: chunk => <p className=''>{chunk}</p>,
                              left_arrow: <span aria-hidden="true">&rarr;</span>
                            }}/>

        </FullBanner>
      }

      <div
        className={classNames([
          'flex',
          'flex-wrap',
          'bg-white',
          'p-2',
          'gap-2',
          // 'md:overflow-x-scroll',
          // 'md:flex-nowrap',
          'md:p-4',
          'md:gap-4',
          'md:shadow',
          'md:sticky',
          'md:top-0',
          'md:left-0',
          'md:z-30',
        ])}
      >
        <Input
          containerProps={{
            className: 'w-full md:w-auto inline-block relative',
          }}
          className="w-full md:w-auto md:max-w-max p-2 text-sm md:text-base md:p-4 focus:border-primary transition duration-200 border"
          placeholder={formatMessage(
            messages[
              roles.includes('coordinator')
                ? 'queryPlaceholderPrivate'
                : 'queryPlaceholderPublic'
            ]
          )}
          value={query}
          onChange={({ target: { value }}) => setQuery(value)}
          onKeyUp={({ code, target }) => {
            if (code == 'Enter') {
              target.blur()
            }
          }}
          onBlur={({ target: { value } }) =>
            dispatch({ type: 'SEARCH', payload: value })
          }
        />

        <Filters dispatch={dispatch} project={project} />

        <SelectAsync
          instanceId="tag-select"
          className="inline-block w-auto relative text-sm md:text-base"
          size={{ sm: 'sm' }[bp] || 'base'}
          styles={{
            container: (provided, state) => ({
              ...provided,
              width: 'max-content',
            }),
          }}
          value={
            project.tags
              .filter((tag) => typeof tag === 'string')
              .map((tag) => ({
                value: tag,
                label: tag.replace(/-/g, ' ').replace(/(^\w|\s\w)/g, m => m.toUpperCase())
              }))
          }
          onChange={(values) =>
            dispatch({
              type: 'SET_TAGS',
              payload: values.map(({ value }) => value),
            })
          }
          isMulti={true}
          placeholder={formatMessage(messages.ToneOfVoice)}
          floatingLabel={formatMessage(messages.ToneOfVoice)}
          defaultOptions
          loadOptions={debounce(
            (query) =>
              request(`/rest/v2/tag?search[name]=${query}`).then((tags) =>
                tags.map(({ name }) => ({ value: name, label: name.replaceAll('-', ' ').replace(/(^\w|\s\w)/g, m => m.toUpperCase()) }))
              ),
            250
          )}
        />

        <select
          value={groupAgeTuple(project.ageTuple)}
          className="md:hidden text-sm w-26 text-gray-500 truncate border border-gray-200 p-1"
          onChange={({ target: { value } }) =>
            dispatch({ type: 'SET_AGES', payload: value.split('-').map((a) => parseInt(a)) })
          }
        >
          <option value={null}>
            {formatMessage(messages.age)}
          </option>
          <option value="0-18">{formatMessage(messages.ageGroupChildren)}</option>
          <option value="19-30">{formatMessage(messages.ageGroupYoung)}</option>
          <option value="31-50">{formatMessage(messages.ageGroupAdult)}</option>
          <option value="51-100">{formatMessage(messages.ageGroupOld)}</option>
          <option value="18-100">{formatMessage(messages.ageGroupNoChildren)}</option>
        </select>

        <fieldset className="hidden md:flex md:w-1/6 border border-gray-200 py-2 px-4 text-gray-500 relative items-center">
          <div className="absolute -top-2 font-medium text-xs transition-all transition-duration-200 bg-white">
            <FormattedMessage {...messages.age}/>
          </div>

          <Range
            onChange={(payload) => dispatch({ type: 'SET_AGES', payload })}
            min={1}
            value={[
              project.voiceages?.[0] || 18,
              project.voiceages?.[1] || 100,
            ]}
          />
        </fieldset>

        <fieldset className="hidden md:flex border border-gray-200 p-2 flex items-center text-gray-500 relative">
          <div className="absolute -top-2 font-medium text-xs transition-all transition-duration-200 bg-white">
            <FormattedMessage {...messages.availability} />
          </div>
          <DateRangePicker
            onCalendarOpen={() => {
              //THIS IS A HACK!
              //When the cookie modal is shown it doesn't get removed from the DOM which causes the calender to be loaded in the middle of the input.
              //The calender uses the <Fit></Fit> component which takes the parent element and in this case it will be the CybotCookiebotDialogBodyUnderlay from the cookie modal.
              //The portalContainer prop does not work in this instance
              document.getElementById('CybotCookiebotDialogBodyUnderlay')?.remove()
            }}
            minDate={new Date()}
            monthPlaceholder="mm"
            yearPlaceholder="yyyy"
            dayPlaceholder="dd"
            format="dd/MM"
            onChange={(dateTupple) =>
              dispatch({ type: 'AVAILABILITY', payload: dateTupple })
            }
            value={[project.availableFrom, project.availableTo]}
            calendarIcon={null}
          />
        </fieldset>

        {roles.includes('coordinator') ? (
          <fieldset className="flex border border-gray-200 p-2 gap-2 items-center text-gray-500 relative">
            <div className="absolute -top-2 font-medium text-xs bg-white">
              <FormattedMessage {...messages.new} />
            </div>

            <select
              value={isCustomNewDate ? 'custom' : project.newSince || ''}
              className="text-sm text-gray-500 bg-transparent"
              onChange={({ target: { value } }) =>
                dispatch({
                  type: 'SET_NEW_SINCE',
                  payload:
                    value == 'custom'
                      ? monthsAgoDate(
                          Number(project.newSince) || NEW_MONTH_OPTIONS[0]
                        )
                      : value,
                })
              }
            >
              <option value="">{formatMessage(messages.newAnyTime)}</option>
              {NEW_MONTH_OPTIONS.map((months) => (
                <option key={`new-${months}`} value={String(months)}>
                  {formatMessage(messages.newLastMonths, { months })}
                </option>
              ))}
              <option value="custom">
                {formatMessage(messages.newCustomDate)}
              </option>
            </select>

            {isCustomNewDate && (
              <input
                type="date"
                className="text-sm text-gray-500 border border-gray-200 p-1"
                max={toDateInputValue(new Date())}
                value={project.newSince}
                onChange={({ target: { value } }) =>
                  dispatch({ type: 'SET_NEW_SINCE', payload: value })
                }
              />
            )}
          </fieldset>
        ) : (
          <fieldset className="flex border border-gray-200 py-2 px-4 items-center text-gray-500 relative">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={!!project.newSince}
                onChange={({ target: { checked } }) =>
                  dispatch({
                    type: 'SET_NEW_SINCE',
                    payload: checked ? String(PUBLIC_NEW_MONTHS) : '',
                  })
                }
              />
              <FormattedMessage {...messages.newOnly} />
            </label>
          </fieldset>
        )}

        {roles.includes('coordinator') && (
          <>
            <SelectAsync
              instanceId="collections-select"
              className="inline-block w-auto relative text-sm md:text-base"
              size={{ sm: 'sm' }[bp] || 'base'}
              styles={{
                container: (provided, state) => ({
                  ...provided,
                  width: 'max-content',
                }),
              }}
              value={project.collections.map(({ name, id }) => ({
                value: id,
                label: name,
              }))}
              onChange={(values) =>
                dispatch({
                  type: 'SET_COLLECTIONS',
                  payload: values.map(({ label, value }) => ({
                    id: value,
                    name: label,
                  })),
                })
              }
              isMulti={true}
              placeholder="Collections"
              defaultOptions
              loadOptions={(query) =>
                request(`/rest/v2/supplier/collections?search[name]=${query}`).then((list) => {
                  const collections = list.map(({ id, name, slug }) => ({
                    id,
                    name: messages[`collections.${slug}`]
                      ? formatMessage(messages[`collections.${slug}`])
                      : name,
                  }))

                  const prevCollectionIds = project.collections.map(
                    ({ id }) => id
                  )

                  if (project.collections.length) {
                    dispatch({
                      type: 'SET_COLLECTIONS',
                      payload: collections.filter(({ id }) =>
                        prevCollectionIds.includes(id)
                      ),
                    })
                  }

                  return collections.map(({ id, name }) => ({
                    value: id,
                    label: name,
                  }))
                })
              }
            />
            <SelectAsync
              instanceId="languagesProficiency-select"
              className="inline-block w-auto relative text-sm md:text-base"
              size={{ sm: 'sm' }[bp] || 'base'}
              styles={{
                container: (provided, state) => ({
                  ...provided,
                  width: 'max-content',
                }),
              }}
              value={project.languagesProficiency.map(({ name, slug }) => ({
                value: slug,
                label: name,
              }))}
              onChange={(values) =>
                dispatch({
                  type: 'SET_LANGUAGES_PROFICIENCY',
                  payload: values.map(({ label, value }) => ({
                    slug: value,
                    name: label,
                  })),
                })
              }
              isMulti={true}
              placeholder="Language Proficiency"
              defaultOptions
              loadOptions={() =>
                request('/rest/v2/language_skill_set').then((list) => {
                  const proficiencies = list.map(({ name, slug }) => ({
                    slug,
                    name: messages[`languagesProficiency.${slug}`]
                      ? formatMessage(messages[`languagesProficiency.${slug}`])
                      : name,
                  }))

                  const prevProficiencySlugs = project.languagesProficiency.map(
                    ({ slug }) => slug
                  )

                  if (project.languagesProficiency.length) {
                    dispatch({
                      type: 'SET_LANGUAGES_PROFICIENCY',
                      payload: proficiencies.filter(({ slug }) =>
                      prevProficiencySlugs.includes(slug)
                      ),
                    })
                  }

                  return proficiencies.map(({ slug, name }) => ({
                    value: slug,
                    label: name,
                  }))
                })
              }
            />
            <Select
              instanceId="demoLanguages-select"
              className="inline-block w-auto relative text-sm md:text-base"
              size={{ sm: 'sm' }[bp] || 'base'}
              styles={{
                container: (provided, state) => ({
                  ...provided,
                  width: 'max-content',
                }),
              }}
              value={languageOptions.filter(({ value }) => {
                if (Array.isArray(project.demoLanguages)) {
                  return project.demoLanguages.includes(value)
                }
                return value === project.demoLanguages
              }) || null}
              onChange={(values) =>
                dispatch({
                  type: 'SET_DEMO_LANGUAGES',
                  payload: values.map(({ value }) => value),
                })
              }
              isMulti={true}
              placeholder={formatMessage(messages.demoLanguages)}
              floatingLabel={formatMessage(messages.demoLanguages)}
              options={languageOptions}
            />

            <Select
              instanceId="demoDialect-select"
              className="inline-block w-auto relative text-sm md:text-base"
              size={{ sm: 'sm' }[bp] || 'base'}
              styles={{
                container: (provided, state) => ({
                  ...provided,
                  width: 'max-content',
                }),
              }}
              value={(project.demoDialects.length && dialectOptions) ?
                dialectOptions.filter((dialect) => 
                  project.demoDialects.includes(String(dialect.uid))
                ).map(({uid, name}) => ({value: uid, label: name})) : null
              }
              onChange={(values) =>
                dispatch({
                  type: 'SET_DEMO_DIALECTS',
                  payload: values.map(({ value }) => String(value)),
                })
              }
              isMulti={true}
              placeholder="Demo Dialect"
              floatingLabel="Demo Dialect"
              options={dialectOptions ? dialectOptions.map((option) => ({value: option.uid, label: option.name})): null}
            />

            <Select
              instanceId="demoAccent-select"
              className="inline-block w-auto relative text-sm md:text-base"
              size={{ sm: 'sm' }[bp] || 'base'}
              styles={{
                container: (provided, state) => ({
                  ...provided,
                  width: 'max-content',
                }),
              }}
              value={project.demoAccents.length ?
                languageOptions.filter((language) => 
                  project.demoAccents.includes(String(language.id))
                ) : null
              }
              onChange={(values) =>
                dispatch({
                  type: 'SET_DEMO_ACCENTS',
                  payload: values.map(({ id }) => String(id)),
                })
              }
              isMulti={true}
              placeholder="Demo Accent"
              floatingLabel="Demo Accent"
              options={languageOptions}
            />

              <Button
                variant="text"
                size="sm"
                icon="user-astronaut"
                onClick={() =>
                  setCharacterMap((map) => {
                    const characterName = window.prompt(formatMessage(messages.characterName))
                    if (!characterName) {
                      return map
                    }
                    return {
                      ...map,
                      [characterName]: [],
                    }
                  })
                }
                className="text-left">
                <FormattedMessage {...messages.addCharacter}/>
              </Button>
          </>
        )}
      </div>
      {!roles.includes('coordinator') &&
        <InitialProcess
          language={
            projectLanguageKeyExists
              ? formatMessage(languageNames[reverseLookupMessage('languages', project?.language, formatMessage)])
              : null
          }
          project={project}
        />
      }

      <div className="pb-32 p-2 md:p-4">
        <div className="flex md:justify-between md:items-center gap-3">
          { loading ? (
            <div className="text-sm space-x-2 font-medium text-gray-400">
              <span>Loading</span>
              <Icon icon="spinner-third" className="animate-spin" />
            </div>
          ) : (
            <div className="md:flex items-center text-sm space-x-3">
              <div className="flex items-center space-x-1">
                <span>
                  {meta?.total < meta?.pageSize ? meta?.total : meta?.pageSize} of{' '}
                  {meta?.total} <span className="hidden md:inline">
                  <FormattedMessage {...messages.voicesOrdered}/>
                  </span>
                </span>
                <select
                  value={project.voiceStars ? 'voicestar' : `${project.order.field}-${project.order.direction}`}
                  className="font-medium truncate text-primary appearance-none lowercase w-auto inline-block"
                  onChange={({ target: { value } }) => {
                    dispatch({
                      type: 'SET_VOICESTAR_COLLECTION',
                      payload: value == 'voicestar',
                    })
                    if (value != 'voicestar') {
                      dispatch({ type: 'SET_ORDER', payload: value.split('-') })
                    }
                  }}
                >
                  <option value="priority-DESC">
                    {formatMessage(messages.sortDirectionBestMatches)}
                  </option>
                  <option value="voicestar">   {formatMessage(messages.sortDirectionRecommended)}</option>
                  <option value="created-DESC">{formatMessage(messages.sortDirectionNewestVoices)}</option>
                  <option value="created-ASC"> {formatMessage(messages.sortDirectionMostExperienced)}</option>
                  <option value="sku-ASC">     {formatMessage(messages.sortDirectionVoiceIDAscending)}</option>
                  <option value="sku-DESC">    {formatMessage(messages.sortDirectionVoiceIDDescending)}</option>
                  { roles.includes('coordinator') &&
                    <>
                        <option value="rate_commercial-ASC"> {formatMessage(messages.sortDirectionCostAscending)}</option>
                        <option value="rate_commercial-DESC">{formatMessage(messages.sortDirectionCostDescending)}</option>
                    </>
                  }
                </select>
                <Icon icon="chevron-down" className="text-sm text-primary" />
              </div>

              <button
                onClick={() => dispatch({ type: 'RESET_FILTERS' })}
                className="text-primary hover:text-primary-darker font-medium"
              >

                <FormattedMessage {...messages.restFiler}/><Icon icon="redo" />
              </button>
            </div>
          )}

          { roles.includes('coordinator') &&
            <span className="text-sm text-gray-400">
                <FormattedMessage {...messages.loggedInMessage} values={{name:name}}/>
                <button className="text-primary hover:font-medium" onClick={onLogOut}>
                    <FormattedMessage {...messages.logOutButton} />
                </button>
            </span>
          }

          <fieldset className="md:flex space-x-3 items-center text-sm">
            { project.includeAllEligable &&
              <button
                onClick={() => dispatch({ type: 'TOGGLE_INCLUDE_ALL' })}
                className="text-primary hover:text-primary-darker font-medium"
              >
                {project.includeAll ? (
                  <>
                    <Icon icon="plus" /><FormattedMessage {...messages.includeSelectedVoiceOnly}/>
                  </>
                ) : (
                  <>
                    <span className="hidden md:block space-x-1">
                      <Icon icon={['fas', 'share']} />
                      { roles.includes('coordinator') ?
                          <span><FormattedMessage {...messages.includeFilterResultsCoordinator}/></span>
                      :
                          <span><FormattedMessage {...messages.includeFilterResultsPublic}/></span>
                      }
                    </span>
                    <span className="md:hidden">
                      <Icon icon="plus" /> <FormattedMessage {...messages.includeAll}/>
                    </span>
                  </>
                )}
              </button>
            }

            <span className="hidden md:inline space-x-2">
              <span>Show as</span>

              {Object.entries({ GRID: 'th-large', LIST: 'th-list' }).map(
                ([key, icon]) => (
                  <button
                    key={`view-${key}`}
                    className={classNames('capitalize space-x-1', {
                      'text-primary': project.view == key,
                    })}
                    onClick={() =>
                      dispatch({ type: 'CHANGE_VIEW', payload: key })
                    }
                  >
                    <span className="font-medium">{key.toLowerCase()}</span>
                    <Icon icon={icon} />
                  </button>
                )
              )}

              { showAiSuggest &&
                <Button
                  size="xs"
                  state="positive"
                  className="bg-positive-darker border-positive-darker"
                  onClick={() => onSuggestWithAI()}
                  icon={['fas', 'search']}
                  iconPosition="right"
                >
                  <FormattedMessage {...messages.buttonAutoSuggestWithAi}/>
                </Button>
              }
            </span>
          </fieldset>
        </div>

        { project.restoreAvailable &&
          <section className="w-full my-4">
            <Banner
              state="success"
              subtitle={formatMessage(messages.saveProgress, {amount: Object.keys(project.restoreAvailable?.voices || {}).length})}
              onClose={() => project.reset()}
            >
              <Button state="positive" variant="plain" size="sm" onClick={() => project.restore()}>
                <FormattedMessage {...messages.restoreProject} />
              </Button>
            </Banner>
          </section>
        }

        <div className="mt-4">
          <VoiceGrid view={project.view}>
            { (!loading || loadingMore) &&
              <>{ voiceGridListState }</>
            }

            { loading && [...Array(9)].map((_, i) => (
              <Card
                key={`loading-${i}`}
                className="h-40 bg-gray-50 break-inside-avoid animate-pulse p-4 space-y-4"
              >
                <div className="bg-gray-100 w-2/3 h-4 block" />
                <div className="bg-gray-100 w-full h-8 block" />
                <div className="bg-gray-100 w-full h-8 block" />
              </Card>
            )) }
          </VoiceGrid>
        </div>


        <div className="w-full pt-10 pb-20 flex justify-center">
          {!loading && (
            <React.Fragment>
              {meta && parseInt(meta.currentPage) * parseInt(meta.pageSize) < parseInt(meta.total) ? (
                <Button
                  onClick={() =>
                    dispatch({
                      type: 'SET',
                      payload: {
                        key: 'currentPage',
                        value: parseInt(project.currentPage) + 1,
                      },
                    })
                  }
                >
                  <FormattedMessage {...messages.loadMore}/>
                </Button>
              ) : (
                <Button disabled>
                  <FormattedMessage {...messages.loadMoreDisable}/>
                </Button>
              )}
            </React.Fragment>
          )}
        </div>
      </div>

      { projectLanguageKeyExists && (
        <div className="bg-gray-50 border-t border-gray-300 py-8 px-8">
          <article className="container mx-auto space-y-4">
            <h2 className="text-xl font-medium">
              <FormattedMessage
                {...languageNames[reverseLookupMessage('languages', project.language, formatMessage)]}
              />
            </h2>
            <MarkdownCopy id="body" className="leading-relaxed">
              <FormattedMessage
                {...messages.body}
                values={{
                  telephoneLink: (chunks) => (
                    <a className="text-primary" href={`tel:${chunks}`}>
                      {chunks}
                    </a>
                  ),
                  emailLink: (chunks) => (
                    <a
                      className="text-primary"
                      href={`mailto:${brand.contactEmail}`}
                    >
                      {chunks}
                    </a>
                  ),
                  language: (
                    <span className="lowercase">
                      { project.language in languageNames ? formatMessage(languageNames[project.language]) : project.language }
                    </span>
                  ),
                }}
              />
            </MarkdownCopy>
          </article>
        </div>
      )}
    </div>
  )
}

export default ExplorePage
